/**
 * Finds the vendor bundle the light preview loads (light-preview plan F2).
 *
 * `scripts/build-preview-vendor.mjs` publishes one directory per template lockfile and writes
 * `current.json` beside it, so the tab discovers the right one at runtime instead of having a
 * hash compiled into it — a dependency change then only needs the vendor build, not a new web
 * bundle. Both files are served from our own origin.
 *
 * There is one bundle per template (US 359): a mobile prototype loads Framework7 and its icon
 * fonts, a web one loads shadcn's Radix packages, and neither carries the other's. `current.json`
 * holds an entry per template; its flat `lockHash`/`publicBase` still point at web so an older
 * reader keeps working.
 */
import type { TemplateId } from '../templates/headlessTemplate';

export interface VendorAssets {
  /** e.g. `/preview-vendor/b3522657a0d6` */
  publicBase: string;
  lockHash: string;
  /** Specifier → URL, for the preview document's import map. */
  importMap: Readonly<Record<string, string>>;
  /** The compiler that runs in the tab. */
  wasmUrl: string;
  /** Tailwind's browser build, or null when the bundle was built without it. */
  tailwindRuntimeUrl: string | null;
  /**
   * Stylesheets that ship inside a package, by name (US 359: `framework7`, `framework7-rtl`).
   * The tab's compiler cannot resolve a bare `.css` specifier, so these are served instead.
   */
  packageStyleUrls: Readonly<Record<string, string>>;
}

export const DEFAULT_VENDOR_ROOT = '/preview-vendor';

/**
 * The preview runs in a blob document, whose base URL is opaque: a path like
 * `/preview-vendor/react.js` cannot be resolved against it, and the browser reports every import
 * as "blocked by a null value" while `<script src>` silently fails. So every URL handed to the
 * document is absolute.
 */
export function absoluteUrl(path: string, origin?: string): string {
  if (/^[a-z]+:/i.test(path)) return path;
  const base = origin ?? (typeof location === 'undefined' ? '' : location.origin);
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/** Thrown when the vendor bundle is missing, so the caller can fall back to the WebContainer. */
export class PreviewVendorUnavailableError extends Error {
  constructor(detail: string) {
    super(
      `The preview dependency bundle is unavailable (${detail}). ` +
        'Run `pnpm --filter @ideoz/workbench build:preview-vendor`.',
    );
    this.name = 'PreviewVendorUnavailableError';
  }
}

export async function loadVendorAssets(
  root: string = DEFAULT_VENDOR_ROOT,
  fetchImpl: typeof fetch = fetch,
  template: TemplateId = 'web',
): Promise<VendorAssets> {
  const current = await readJson(`${root}/current.json`, fetchImpl);
  const entry = pickTemplateEntry(current, template);
  const publicBase =
    typeof entry.publicBase === 'string' ? entry.publicBase : `${root}/${entry.lockHash}`;
  const map = await readJson(`${publicBase}/importmap.json`, fetchImpl);
  const imports = map.imports;
  if (!imports || typeof imports !== 'object') {
    throw new PreviewVendorUnavailableError(`${publicBase}/importmap.json has no imports`);
  }

  // A bundle without a manifest still works; only the Tailwind question goes unanswered.
  const manifest: Record<string, unknown> = await readJson(
    `${publicBase}/manifest.json`,
    fetchImpl,
  ).catch(() => ({}) as Record<string, unknown>);

  const absoluteImports: Record<string, string> = {};
  for (const [specifier, url] of Object.entries(imports as Record<string, string>)) {
    absoluteImports[specifier] = absoluteUrl(url);
  }

  const packageStyles = (manifest.packageStyles ?? {}) as Record<string, string>;
  const packageStyleUrls: Record<string, string> = {};
  for (const [name, file] of Object.entries(packageStyles)) {
    packageStyleUrls[name] = absoluteUrl(`${publicBase}/${file}`);
  }

  return {
    publicBase,
    lockHash: String(entry.lockHash ?? ''),
    importMap: absoluteImports,
    packageStyleUrls,
    wasmUrl: absoluteUrl(`${publicBase}/esbuild.wasm`),
    tailwindRuntimeUrl:
      manifest.tailwindVersion === null
        ? null
        : absoluteUrl(`${publicBase}/tailwind-browser.js`),
  };
}

/**
 * The entry for one template, falling back to the flat fields. A mobile preview must never
 * silently load the web bundle: Framework7 would be missing from the import map and every screen
 * would fail to resolve, which is far more confusing than being told the bundle is not built.
 */
function pickTemplateEntry(
  current: Record<string, unknown>,
  template: TemplateId,
): Record<string, unknown> {
  const templates = current.templates as Record<string, Record<string, unknown>> | undefined;
  const entry = templates?.[template];
  if (entry) return entry;
  if (template === 'web') return current;
  throw new PreviewVendorUnavailableError(
    `current.json has no "${template}" template entry — run ` +
      `\`pnpm --filter @ideoz/workbench build:preview-vendor\``,
  );
}

async function readJson(
  url: string,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetchImpl(url);
  } catch (err) {
    throw new PreviewVendorUnavailableError(
      `${url} could not be fetched: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!response.ok) {
    throw new PreviewVendorUnavailableError(`${url} returned ${response.status}`);
  }
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    throw new PreviewVendorUnavailableError(`${url} is not JSON`);
  }
}
